import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Maximize, Bed, Bath, ShieldCheck, FileText, Share2, Heart, MessageSquare, Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { properties, formatNaira } from '../data/mockData';
import { VerificationBadge, TierBadge } from '../components/shared/Badges';
export const PropertyDetail = () => {
  const {
    id
  } = useParams();
  const property = properties.find(p => p.id === id) || properties[0];
  const images = property.images || [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = (idx: number) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const next = () => setLightboxIndex(i => i === null ? null : (i + 1) % images.length);
  const prev = () => setLightboxIndex(i => i === null ? null : (i - 1 + images.length) % images.length);
  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);
  const extraCount = Math.max(0, images.length - 3);
  return <div className="min-h-screen bg-neutral-light pt-24 pb-20" data-id="element-1872">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1873">
        {/* Breadcrumb & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1874">
          <div className="text-sm text-text-light flex items-center gap-2" data-id="element-1875">
            <Link to="/explore" className="hover:text-primary-900 transition-colors" data-id="element-1876">
              Explore
            </Link>
            <span data-id="element-1877">/</span>
            <span className="text-primary-900 font-medium" data-id="element-1878">
              {property.district}
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-1879">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-1880">
              <Share2 size={16} data-id="element-1881" /> Share
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-1882">
              <Heart size={16} data-id="element-1883" /> Save
            </button>
          </div>
        </div>

        {/* Image Gallery — all tiles open the lightbox */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 h-[400px] md:h-[500px]" data-id="element-1884">
          <button type="button" onClick={() => openLightbox(0)} className="md:col-span-3 h-full rounded-2xl overflow-hidden relative group cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-secondary" aria-label="View photo 1 in fullscreen" data-id="element-1885">
            <img src={images[0]} alt={property.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1886" />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" data-id="element-1887" />
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-primary-900 text-xs font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1888">
              Click to expand
            </div>
          </button>
          <div className="hidden md:flex flex-col gap-4 h-full" data-id="element-1889">
            <button type="button" onClick={() => openLightbox(1)} className="w-full h-1/2 rounded-2xl overflow-hidden cursor-zoom-in group focus:outline-none focus:ring-2 focus:ring-secondary" aria-label="View photo 2 in fullscreen" data-id="element-1890">
              <img src={images[1] || images[0]} alt={`${property.title} — photo 2`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1891" />
            </button>
            <button type="button" onClick={() => openLightbox(2)} className="w-full h-1/2 relative rounded-2xl overflow-hidden cursor-zoom-in group focus:outline-none focus:ring-2 focus:ring-secondary" aria-label={`View remaining photos (${images.length} total)`} data-id="element-1892">
              <img src={images[2] || images[0]} alt={`${property.title} — photo 3`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1893" />
              {extraCount > 0 && <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/60 transition-colors" data-id="element-1894">
                  <span className="text-white font-bold text-lg" data-id="element-1895">
                    +{extraCount} Photos
                  </span>
                </div>}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-1896">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-1897">
            {/* Header Info */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1898">
              <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-1899">
                <TierBadge tier={property.tier as 1 | 2 | 3} data-id="element-1900" />
                {property.isVerified && <VerificationBadge status="Approved" data-id="element-1901" />}
                <span className="px-3 py-1 bg-neutral-100 text-text-light text-xs font-medium rounded-full" data-id="element-1902">
                  {property.type}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4 leading-tight" data-id="element-1903">
                {property.title}
              </h1>
              <div className="flex items-center text-text-light mb-6" data-id="element-1904">
                <MapPin size={18} className="mr-2 text-secondary" data-id="element-1905" />
                {property.district}, Abuja, FCT
              </div>
              <div className="text-3xl font-bold text-primary-900" data-id="element-1906">
                {formatNaira(property.price)}
              </div>
            </div>

            {/* Key Features */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1907">
              <h2 className="text-xl font-serif font-bold text-primary-900 mb-6" data-id="element-1908">
                Property Overview
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1909">
                <div className="flex flex-col gap-1" data-id="element-1910">
                  <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1911">
                    <Maximize size={16} data-id="element-1912" /> Size
                  </span>
                  <span className="font-semibold text-text" data-id="element-1913">
                    {property.size}
                  </span>
                </div>
                {property.bedrooms > 0 && <div className="flex flex-col gap-1" data-id="element-1914">
                    <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1915">
                      <Bed size={16} data-id="element-1916" /> Bedrooms
                    </span>
                    <span className="font-semibold text-text" data-id="element-1917">
                      {property.bedrooms}
                    </span>
                  </div>}
                {property.bathrooms > 0 && <div className="flex flex-col gap-1" data-id="element-1918">
                    <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1919">
                      <Bath size={16} data-id="element-1920" /> Bathrooms
                    </span>
                    <span className="font-semibold text-text" data-id="element-1921">
                      {property.bathrooms}
                    </span>
                  </div>}
                <div className="flex flex-col gap-1" data-id="element-1922">
                  <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1923">
                    <FileText size={16} data-id="element-1924" /> Title
                  </span>
                  <span className="font-semibold text-text" data-id="element-1925">
                    {property.ownership}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification Status */}
            <div className="bg-primary-50 p-6 md:p-8 rounded-2xl border border-primary-100" data-id="element-1926">
              <div className="flex items-center gap-3 mb-6" data-id="element-1927">
                <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center text-success" data-id="element-1928">
                  <ShieldCheck size={24} data-id="element-1929" />
                </div>
                <div data-id="element-1930">
                  <h2 className="text-xl font-serif font-bold text-primary-900" data-id="element-1931">
                    Verification Report
                  </h2>
                  <p className="text-sm text-text-light" data-id="element-1932">
                    Verified by AMU Compliance Team
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1933">
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1934">
                  <div className="flex items-center gap-3" data-id="element-1935">
                    <CheckCircle className="text-success" size={20} data-id="element-1936" />
                    <span className="font-medium text-text" data-id="element-1937">
                      Title Document (C of O)
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1938">
                    VERIFIED
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1939">
                  <div className="flex items-center gap-3" data-id="element-1940">
                    <CheckCircle className="text-success" size={20} data-id="element-1941" />
                    <span className="font-medium text-text" data-id="element-1942">
                      Seller Identity (KYC)
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1943">
                    VERIFIED
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1944">
                  <div className="flex items-center gap-3" data-id="element-1945">
                    <CheckCircle className="text-success" size={20} data-id="element-1946" />
                    <span className="font-medium text-text" data-id="element-1947">
                      AGIS/FCDA Record Match
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1948">
                    VERIFIED
                  </span>
                </div>
              </div>
            </div>

            {/* Map Mock */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1949">
              <h2 className="text-xl font-serif font-bold text-primary-900 mb-6" data-id="element-1950">
                Location
              </h2>
              <div className="w-full h-[300px] bg-neutral-200 rounded-xl overflow-hidden relative" data-id="element-1951">
                <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1000" alt="Map" className="w-full h-full object-cover opacity-60" data-id="element-1952" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1953">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-white shadow-glow animate-pulse" data-id="element-1954">
                    <MapPin size={24} data-id="element-1955" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-1956">
            {/* Seller Card */}
            <div className="bg-white p-6 rounded-2xl shadow-soft border border-neutral-200 sticky top-24" data-id="element-1957">
              <h3 className="text-sm font-bold text-text-light uppercase tracking-wider mb-4" data-id="element-1958">
                Listed By
              </h3>

              <div className="flex items-center gap-4 mb-6" data-id="element-1959">
                <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center text-primary-900 font-serif font-bold text-2xl border border-primary-100" data-id="element-1960">
                  {property.seller.name.charAt(0)}
                </div>
                <div data-id="element-1961">
                  <h4 className="font-bold text-text text-lg" data-id="element-1962">
                    {property.seller.name}
                  </h4>
                  <div className="flex items-center gap-2 mt-1" data-id="element-1963">
                    <VerificationBadge status="Approved" type="seller" data-id="element-1964" />
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6" data-id="element-1965">
                <button className="w-full bg-primary-900 hover:bg-primary-800 text-white py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-1966">
                  <Calendar size={18} data-id="element-1967" /> Book Inspection
                </button>
                <button className="w-full bg-white border-2 border-primary-900 text-primary-900 hover:bg-primary-50 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-1968">
                  <MessageSquare size={18} data-id="element-1969" /> Message Seller
                </button>
              </div>

              <div className="p-4 bg-neutral-50 rounded-xl text-sm text-text-light border border-neutral-100 text-center" data-id="element-1970">
                Transactions are secure. Never pay outside the AMU platform
                before an inspection.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col animate-in fade-in" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Property photo gallery" data-id="element-1971">
          {/* Top bar */}
          <div className="flex items-center justify-between p-4 md:p-6 text-white" data-id="element-1972">
            <div className="text-sm font-medium" data-id="element-1973">
              {lightboxIndex + 1} / {images.length}
            </div>
            <button type="button" onClick={e => {
          e.stopPropagation();
          closeLightbox();
        }} aria-label="Close gallery" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-1974">
              <X size={20} data-id="element-1975" />
            </button>
          </div>

          {/* Image */}
          <div className="flex-grow flex items-center justify-center px-4 md:px-12 relative" onClick={e => e.stopPropagation()} data-id="element-1976">
            <img src={images[lightboxIndex]} alt={`${property.title} — photo ${lightboxIndex + 1}`} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" data-id="element-1977" />

            {images.length > 1 && <>
                <button type="button" onClick={prev} aria-label="Previous photo" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" data-id="element-1978">
                  <ChevronLeft size={24} data-id="element-1979" />
                </button>
                <button type="button" onClick={next} aria-label="Next photo" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" data-id="element-1980">
                  <ChevronRight size={24} data-id="element-1981" />
                </button>
              </>}
          </div>

          {/* Thumbnail strip */}
          {images.length > 1 && <div className="p-4 md:p-6 flex gap-2 justify-center overflow-x-auto" onClick={e => e.stopPropagation()} data-id="element-1982">
              {images.map((src, idx) => <button key={idx} type="button" onClick={() => setLightboxIndex(idx)} aria-label={`Go to photo ${idx + 1}`} className={`relative w-20 h-14 md:w-24 md:h-16 rounded-md overflow-hidden border-2 transition-all shrink-0 ${idx === lightboxIndex ? 'border-secondary ring-2 ring-secondary/40' : 'border-transparent opacity-50 hover:opacity-100'}`} data-id="element-1983">
                  <img src={src} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" data-id="element-1984" />
                </button>)}
            </div>}
        </div>}
    </div>;
};
const CheckCircle = ({
  className,
  size
}: {
  className?: string;
  size?: number;
}) => <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1985">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-1986"></path>
    <polyline points="22 4 12 14.01 9 11.01" data-id="element-1987"></polyline>
  </svg>;